import React from "react";
import { PiHouse } from "react-icons/pi";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import PageLoader from "components/ui/PageLoader";
import { Link, useParams } from "react-router-dom";
import ImageUpload from "components/contractor/ImageUpload";
import FloorplanUpload from "components/contractor/FloorplanUpload";
import VideoUpload from "components/contractor/VideoUpload";
import VideoUploadEdit from "components/contractor/VideoUploadEdit";
import AdminServiceImageUpload from "components/contractor/ServiceImageUpload";
import { useAdminContractorEventsQuery } from "apis/event";

const STATUS_STYLES = {
  overdue: {
    bg: "bg-red-50",
    text: "text-red-700",
    dot: "bg-red-500",
    label: "Overdue",
  },
  revisionSent: {
    bg: "bg-red-50",
    text: "text-red-700",
    dot: "bg-red-500",
    label: "Revision Sent",
  },
  revisionInProgress: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-500",
    label: "In Progress",
  },
  today: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    dot: "bg-blue-500",
    label: "Today",
  },
  upcoming: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-500",
    label: "Upcoming",
  },
  completed: {
    bg: "bg-green-50",
    text: "text-green-700",
    dot: "bg-green-500",
    label: "Completed",
  },
  revisionCompleted: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    dot: "bg-blue-500",
    label: "Revision Done",
  },
  approved: {
    bg: "bg-green-50",
    text: "text-green-700",
    dot: "bg-green-500",
    label: "Approved",
  },
};

const CATEGORY_ORDER = [
  "revisionSent",
  "revisionInProgress",
  "overdue",
  "today",
  "upcoming",
  "completed",
  "revisionCompleted",
  "approved",
];

// eventTypeKeys that are photo-type services
const PHOTO_SERVICE_KEYS = [
  "photo",
  "photoReshoot",
  "photoFreeShoot",
  "dusk",
  "drone",
  "duskGolden",
];

const formatDate = (dateString) => {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Australia/Sydney",
    hour12: true,
  }).format(new Date(dateString));
};

const StatusBadge = ({ status }) => {
  const c = STATUS_STYLES[status] || STATUS_STYLES.upcoming;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${c.bg} ${c.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
};

const EventJob = ({ event, category }) => {
  const hasServices = event?.services?.length > 0;

  // Extract photo-type services from services[]
  const photoServices = hasServices
    ? event.services.filter((s) => PHOTO_SERVICE_KEYS.includes(s.eventTypeKey))
    : [];
  const hasPhotoServices = photoServices.length > 0;

  // Check for floorplan/video in services[]
  const hasFloorplanService =
    hasServices &&
    event.services.some((s) =>
      ["floorplan", "floorplanRedraw"].includes(s.eventTypeKey)
    );
  const hasVideoService =
    hasServices &&
    event.services.some((s) =>
      ["video", "storyVideo"].includes(s.eventTypeKey)
    );

  // OLD PATH fallback — only used when services[] is empty/missing
  const legacyService = !hasServices ? event?.servicesOffered?.[0] : null;

  return (
    <div className="bg-white border border-mediumgray rounded-lg overflow-hidden">
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h4 className="text-[13px] font-semibold text-black m-0 leading-tight">
            {event.summary}
          </h4>
          <StatusBadge status={category} />
        </div>

        <div className="flex items-center gap-1.5 text-darkergray text-xs mb-1">
          <PiHouse size={13} className="text-darkergray flex-shrink-0" />
          {event?.propertyId?.address}
        </div>

        {event?.end && (
          <span className="text-[11px] text-darkergray">
            {formatDate(event.end)}
          </span>
        )}
      </div>

      <div className="px-4 pb-4 pt-2 border-t border-mediumgray">
        {/* ═══ NEW PATH: services[] exists ═══ */}

        {/* Photo-type services → tabbed ServiceImageUpload */}
        {hasPhotoServices && (
          <AdminServiceImageUpload
            eventProp={event}
            photoServices={photoServices}
          />
        )}

        {/* Floorplan service */}
        {hasFloorplanService && (
          <div
            className={
              hasPhotoServices ? "mt-4 pt-4 border-t border-mediumgray" : ""
            }
          >
            <FloorplanUpload eventProp={event} />
          </div>
        )}

        {/* Video service */}
        {hasVideoService && (
          <div
            className={
              hasPhotoServices || hasFloorplanService
                ? "mt-4 pt-4 border-t border-mediumgray"
                : ""
            }
          >
            <div className="flex flex-col gap-4">
              <VideoUpload
                eventProp={event}
                folderPath="videos/raw"
                type="Raw"
              />
              <VideoUploadEdit eventProp={event} folderPath="videos/edited" />
            </div>
          </div>
        )}

        {/* ═══ OLD PATH: no services[] → legacy servicesOffered ═══ */}
        {legacyService === "Photographer" && <ImageUpload eventProp={event} />}
        {(legacyService === "Floor planner" ||
          legacyService === "Floorplan Redraw") && (
          <FloorplanUpload eventProp={event} />
        )}
        {legacyService === "Videographer" && (
          <div className="flex flex-col gap-4">
            <VideoUpload eventProp={event} folderPath="videos/raw" type="Raw" />
            <VideoUploadEdit eventProp={event} folderPath="videos/edited" />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * One row per job. Deliberately inert — no upload widget is mounted here, so
 * a property with many events costs nothing to list. The widgets mount only
 * on the job route below.
 */
const EventRow = ({ event, category, propertyId }) => (
  <Link
    to={`/edits/${propertyId}/contractor/${event._id}`}
    className="bg-white border border-mediumgray rounded-lg px-4 py-3 flex items-center gap-3 hover:border-black transition-colors no-underline text-inherit"
  >
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h4 className="text-[13px] font-semibold text-black m-0 leading-tight">
          {event.summary}
        </h4>
        <StatusBadge status={category} />
      </div>
      <div className="flex items-center gap-1.5 text-darkergray text-xs mb-1">
        <PiHouse size={13} className="text-darkergray flex-shrink-0" />
        <span className="truncate">{event?.propertyId?.address}</span>
      </div>
      {event?.end && (
        <span className="text-[11px] text-darkergray">
          {formatDate(event.end)}
        </span>
      )}
    </div>
    <FiChevronRight size={16} className="text-darkergray flex-none" />
  </Link>
);

const UploadContractor = () => {
  const { propertyId, eventId } = useParams();
  const { data: events, isLoading } = useAdminContractorEventsQuery(propertyId);

  const eventsData = events || {
    revisionSent: [],
    revisionInProgress: [],
    revisionCompleted: [],
    overdue: [],
    today: [],
    upcoming: [],
    completed: [],
    approved: [],
  };

  const allEvents = CATEGORY_ORDER.flatMap((category) =>
    (eventsData[category] || []).map((event) => ({
      ...event,
      _category: category,
    }))
  );

  if (isLoading) return <PageLoader />;

  // ── One job, on its own route ──────────────────────────────────────────
  // The sticky shot bar needs the viewport, and mounting every job's widgets
  // in a list was what made this page heavy. The tab shell stays above.
  if (eventId) {
    const job = allEvents.find((e) => String(e._id) === String(eventId));
    return (
      <div className="container">
        <Link
          to={`/edits/${propertyId}`}
          className="inline-flex items-center gap-1 text-xs text-darkergray hover:text-black mb-3 no-underline"
        >
          <FiChevronLeft size={14} /> All events
        </Link>
        {job ? (
          <EventJob event={job} category={job._category} />
        ) : (
          <p className="text-sm text-darkergray">
            This event isn’t on this property. It may have been moved or
            deleted.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="container">
      {allEvents.length === 0 ? (
        <div className="text-center py-12 bg-lightergray rounded-lg border border-dashed border-mediumgray">
          <p className="m-0 text-sm text-darkergray font-medium">
            No events found.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {allEvents.map((event, index) => (
            <EventRow
              key={event._id || index}
              event={event}
              category={event._category}
              propertyId={propertyId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default UploadContractor;
